import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ScanFace } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { useTryOn } from "../context/TryOnContext";
import { fmtEUR, fmtXOF } from "../lib/api";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem, openCart } = useCart();
  const { toggle } = useTryOn();
  const { t } = useTranslation();

  const catLabel = () => {
    if (product.sub) return product.sub;
    if (product.category === "accessoires" || product.category === "maroquinerie") return t("nav.leather");
    if (product.category === "homme") return t("nav.man");
    if (product.category === "femme") return t("nav.woman");
    if (product.category === "couple") return t("nav.couple");
    return t("nav.kids");
  };

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, product.sizes?.[0] || "Unique");
    toast.success(t("common.added", { name: product.name }));
    openCart();
  };

  const tryOn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product);
    toast.success(t("tryon.tried", { name: product.name }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        data-testid={`product-card-${product.slug}`}
        to={`/produit/${product.slug}`}
        className="gold-frame group relative block overflow-hidden border border-ligne bg-card2"
      >
        <div className="frame-inner relative aspect-[3/4] overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          />
          {product.badge && (
            <span className="absolute top-4 start-4 bg-noir/80 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-or backdrop-blur-sm">
              {product.badge}
            </span>
          )}
          <div className="absolute inset-x-4 bottom-4 flex translate-y-16 gap-2 opacity-0 transition-all duration-400 group-hover:translate-y-0 group-hover:opacity-100">
            <button
              data-testid={`quick-add-button-${product.slug}`}
              onClick={quickAdd}
              className="flex flex-1 items-center justify-center gap-2 bg-or py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-noir transition-colors hover:bg-orclair"
            >
              <Plus size={13} /> {t("common.quickAdd")}
            </button>
            <button
              data-testid={`tryon-button-${product.slug}`}
              onClick={tryOn}
              aria-label={t("tryon.tried", { name: product.name })}
              title={t("tryon.tryButtonFull", "Essayer virtuellement")}
              className="flex w-12 items-center justify-center bg-noir/80 text-or backdrop-blur-sm transition-colors hover:bg-or hover:text-noir"
            >
              <ScanFace size={15} />
            </button>
          </div>
        </div>
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div>
            <h3 className="font-serif text-lg leading-snug text-ivoire transition-colors group-hover:text-or">
              {product.name}
            </h3>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-argile">
              {catLabel()}
            </p>
          </div>
          <div className="text-end">
            <p className="text-sm text-or">{fmtXOF(product.price_xof)}</p>
            <p className="text-[11px] text-argile">{fmtEUR(product.price_xof)}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
